const toExponent = (currencyExponent) => {
  const parsed = Number(currencyExponent);
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : 0;
};

const factorFor = (currencyExponent) => 10 ** toExponent(currencyExponent);

// The Tier API stores integer minor units. 15000 USD minor units are displayed as "150".
export const minorToMajorInput = (minorAmount, currencyExponent) => {
  const minor = Number(minorAmount);
  if (!Number.isFinite(minor)) return "0";

  const exponent = toExponent(currencyExponent);
  if (exponent === 0) return String(Math.trunc(minor));

  const sign = minor < 0 ? "-" : "";
  const absolute = Math.abs(Math.trunc(minor));
  const factor = factorFor(exponent);
  const whole = Math.floor(absolute / factor);
  const fraction = String(absolute % factor)
    .padStart(exponent, "0")
    .replace(/0+$/, "");
  return fraction ? `${sign}${whole}.${fraction}` : `${sign}${whole}`;
};

// Avoid floating-point multiplication when returning a UI value to the minor-unit API contract.
export const majorToMinor = (majorAmount, currencyExponent) => {
  const value = String(majorAmount ?? "").trim();
  const exponent = toExponent(currencyExponent);
  const match = /^(\d+)(?:\.(\d*))?$/.exec(value);
  if (!match) return null;

  const [, wholePart, enteredFraction = ""] = match;
  if (enteredFraction.length > exponent) return null;

  const whole = Number(wholePart);
  const fraction =
    exponent === 0 ? 0 : Number(enteredFraction.padEnd(exponent, "0") || "0");
  const result = whole * factorFor(exponent) + fraction;
  return Number.isSafeInteger(result) ? result : null;
};

export const currencyStep = (currencyExponent) => {
  const exponent = toExponent(currencyExponent);
  return exponent === 0 ? "1" : `0.${"0".repeat(exponent - 1)}1`;
};

export const formatMinorCurrency = (minorAmount, currencyCode, currencyExponent) => {
  const minor = Number(minorAmount);
  const code =
    typeof currencyCode === "string" ? currencyCode.trim().toUpperCase() : "";
  if (!Number.isFinite(minor) || !code)
    return minorToMajorInput(minorAmount, currencyExponent);

  const exponent = toExponent(currencyExponent);
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: code,
    minimumFractionDigits: 0,
    maximumFractionDigits: exponent,
  }).format(minor / factorFor(exponent));
};
